import React, { useEffect, useState } from 'react';
import { usePower } from '../state/PowerContext';
import { getHighestActuals } from '../api';
import { todayISO } from '../utils/billingPeriod';

function hourLabel(h) {
  const hh = ((h - 1) % 12) + 1;
  const ampm = h >= 12 && h < 24 ? 'PM' : 'AM';
  return `${hh} ${ampm}`;
}

// Pull highest_actuals for the months spanned by the current period (typically 1-2),
// group by year, show the top of the month for prior years alongside this year's running high.
export default function HistoricalPeaks() {
  const { activePeriod, series } = usePower();
  const [monthsData, setMonthsData] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    const months = new Set();
    months.add(activePeriod.startISO.slice(0, 7));
    months.add(activePeriod.endISO.slice(0, 7));
    months.add(todayISO().slice(0, 7));

    Promise.all(
      Array.from(months).map(m => getHighestActuals(m).then(d => [m, d]).catch(() => [m, null]))
    ).then(entries => {
      if (cancelled) return;
      const map = {};
      for (const [m, d] of entries) map[m] = d;
      setMonthsData(map);
      setLoading(false);
    });
    return () => { cancelled = true; };
  }, [activePeriod.startISO, activePeriod.endISO]);

  if (loading) {
    return <div className="panel"><h2>Historical peaks</h2><div className="loading">Loading…</div></div>;
  }

  // Flatten results for the active series, group by (year, month) → top hour
  const rows = [];
  for (const [month, data] of Object.entries(monthsData)) {
    if (!data?.results) continue;
    for (const r of data.results) {
      if ((r.type || '').toUpperCase() !== series) continue;
      rows.push({ month, ...r, year: r.date.slice(0, 4) });
    }
  }

  // Top per (year, month-number)
  const topByYearMonth = {};
  for (const r of rows) {
    const key = `${r.year}_${r.month}`;
    if (!topByYearMonth[key] || r.value > topByYearMonth[key].value) {
      topByYearMonth[key] = r;
    }
  }
  const allTops = Object.values(topByYearMonth).sort((a, b) =>
    b.year.localeCompare(a.year) || b.month.localeCompare(a.month)
  );

  if (allTops.length === 0) {
    return (
      <div className="panel">
        <h2>Historical peaks ({series})</h2>
        <div style={{ color: 'var(--text-dim)' }}>No historical data available for these months.</div>
      </div>
    );
  }

  return (
    <div className="panel">
      <h2>Historical peaks ({series})</h2>
      <table className="hist-table">
        <thead>
          <tr>
            <th>Month</th>
            <th>Date</th>
            <th>Hour</th>
            <th className="num">Value</th>
            <th className="num">Temp (°F)</th>
          </tr>
        </thead>
        <tbody>
          {allTops.map((r, i) => (
            <tr key={i}>
              <td>{r.month}</td>
              <td>{r.date}</td>
              <td>{hourLabel(r.hour)}</td>
              <td className="num">{Number(r.value).toLocaleString(undefined, { maximumFractionDigits: 1 })}</td>
              <td className="num">{r.temperature ?? '—'}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <div style={{ fontSize: 11, color: 'var(--text-dim)', marginTop: 8 }}>
        Shows the monthly top hour (actual) across all years of loaded data for the months your current billing period spans.
      </div>
    </div>
  );
}
